<template>
  <header
    class="surface-section-title section-title"
    :data-view-mode="isBeingEdited ? 'edit' : 'show'"
    @dblclick="tryStartEditing"
  >
    <div class="section-frame">
      <div class="real-section">
        <hgroup v-if="!isBeingEdited">
          <h2 class="title" dir="auto">{{ sectionTitle }}</h2>
        </hgroup>
        <hgroup v-if="isBeingEdited" class="is-editing">
          <!-- N.B. Need @paste.native.stop or the paste event will propagate up to surface container and create a new post -->
          <multiline-input
            ref="title"
            dir="auto"
            class="title"
            :value="sectionTitle"
            :maxlength="maxLength"
            :placeholder="titlePlaceholder"
            required
            @enter="saveTitle"
            @escape="stopEditingSection"
            @paste.native.stop
          />
        </hgroup>
      </div>
      <div v-if="isBeingEdited" class="button-list">
        <button v-if="!isEmptyState" class="button text-button" :title="__('Cancel')" @click="stopEditingSection">
          {{ __('Cancel') }}
        </button>
        <button class="button text-button" :title="__('Done')" @click="saveTitle">{{ __('Save') }}</button>
      </div>
      <button
        v-if="isEditable && !isBeingEdited"
        class="surface-section-actions icon-button"
        :title="__('Section actions')"
        @click="showSectionActionMenu({ sectionId: section.id })"
      >
        <base-icon name="more_vert" />
      </button>
    </div>
  </header>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { mapState, mapGetters, mapActions } from 'vuex'
import BaseIcon from 'vuecomponents/base_icon'
import MultilineInput from 'vuecomponents/multiline_input'
import OutsideEventsMixin from 'vuemixins/outside_events_mixin'

export default {
  components: { MultilineInput, BaseIcon },
  mixins: [OutsideEventsMixin],
  props: ['section', 'sectionBeingEditedId'],
  data() {
    return {
      maxLength: 512,
    }
  },
  computed: {
    ...mapState(['isWallOnboardingFlow', 'isIOS']),
    ...mapGetters('section', {
      wallSections: 'sections',
    }),
    ...mapGetters({
      isEditable: 'canIModerate',
      canIEdit: 'canIEdit',
    }),
    sectionTitle() {
      return this.section.title
    },
    canEdit() {
      return this.canIEdit
    },
    isBeingEdited() {
      // We need to also check that we're not in the first view flow for a section title to be
      // in the edit state, otherwise
      // the first view panel cannot get focus -- it'll keep dithering between the section
      // title and the first view panel's text fields.
      return this.sectionBeingEditedId && this.sectionBeingEditedId === this.section.id && !this.isWallOnboardingFlow
    },
    wallSectionsLength() {
      return this.wallSections.length
    },
    isEmptyState() {
      return this.isBeingEdited && this.wallSectionsLength === 1
    },
    titlePlaceholder() {
      if (this.isEmptyState) {
        return this.__('Name your first section')
      }
      return this.__('Name your section')
    },
  },
  watch: {
    isBeingEdited(val) {
      if (val) {
        this.$nextTick(() => {
          this.$refs.title.focus()
        })
      }
    },
  },
  mounted() {
    if (this.isBeingEdited) {
      this.$nextTick(() => {
        this.$refs.title.focus()
      })
    }
  },
  methods: {
    ...mapActions('section', [
      'changeSectionTitle',
      'stopEditingSection',
      'startEditingSection',
      'showSectionActionMenu',
    ]),
    ...mapActions('snackbars', ['setSnackbar']),
    saveTitle() {
      const sanitizedTitle = this.$refs.title.$el.value.replace(/\n|\r/g, '')
      if (sanitizedTitle.length > 0) {
        this.changeSectionTitle({ sectionId: this.section.id, title: sanitizedTitle })
      } else {
        const message = this.__('Section title cannot be empty.')
        this.setSnackbar({ message })
      }
    },
    tryStartEditing() {
      if (this.canEdit && !this.isBeingEdited) {
        this.startEditingSection({ sectionId: this.section.id })
      }
    },
    deleteSection() {
      this.$emit('delete')
    },
    onOutsideClick() {
      if (!this.isBeingEdited) return

      const sanitizedTitle = this.$refs.title.$el.value.replace(/\n|\r/g, '')
      if (sanitizedTitle.length > 0) {
        this.changeSectionTitle({ sectionId: this.section.id, title: sanitizedTitle })
      } else {
        this.stopEditingSection()
      }
    },
  },
}
</script>

<style lang="scss">
@import './../../assets/stylesheets/3/modules/all';
.surface-section-title {
  position: relative;
  display: block;
  width: 100%;
  box-sizing: border-box;

  margin-bottom: 16px;

  @include respondToPhone {
    margin-bottom: 8px;
  }

  // should be similar to .subject in wishes/words.scss
  .title {
    position: relative;
    color: inherit;
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    text-shadow: inherit;
    text-transform: inherit;
    line-height: inherit;
    word-wrap: break-word;
    white-space: pre-wrap;
    touch-action: manipulation;
    box-shadow: none !important;
    border: none;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    padding: 0;
    margin: 0;
  }

  .button-list {
    padding: 4px 8px;
    justify-content: flex-end;
  }

  .surface-section-actions {
    position: absolute;
    top: 0;
    right: 0;
    width: 40px;
    height: 40px;
    overflow: hidden;

    & > i {
      position: absolute;
      top: 4px;
      right: -3px;
    }
  }
}
</style>
